import type { Except, Optional } from '@festina-life/core'
import cx from 'classnames'
import { type ChangeEvent, type ComponentProps, useState } from 'react'

import { buttonStyles } from '../../actions/button/buttonStyles'
import { IconButton } from '../../actions/iconButton/IconButton'
import { useTranslate } from '../../hooks/useTranslate'
import { Icon } from '../../structure/icon/Icon'
import { type InputProps, InputWrapper } from '../inputWrapper/InputWrapper'
import type { RawTextInputProps } from '../textInput/RawTextInput'
import { createInternalWrapperPropsAndId } from '../utility'

export type FileInputProps = Except<
  RawTextInputProps,
  'type' | 'onChange' | 'value'
> &
  InputProps &
  Pick<ComponentProps<'input'>, 'accept' | 'multiple'> & {
    value: Optional<File[]>
    onChange: (value: Optional<File[]>) => void
    onTouched: () => void
    uploadMessage?: Optional<string>
  }

/**
 * File input that can be used in Festina Forms to display a file input that follows our styling rules.
 */
export const FileInput = ({
  value,
  onChange,
  onTouched,
  wrapperProps,
  isInError,
  uploadMessage,
  ...fieldProps
}: FileInputProps) => {
  // Clearing the current value of a hidden html file input cannot be done by simply setting it's value to null or ''.
  // The hidden input has to be remounted in order to discard its internal state. Otherwise clearing the value will trigger an onChange([]) event
  // to the parent, but the internal input state will remain unchanged. As a result of this attempting to reassign the old value to the input will
  // fail since the input will internally still be pointing to that value and not trigger an onChange event.
  const [inputKey, setInputKey] = useState(crypto.randomUUID())
  const { internalWrapperProps, id } = createInternalWrapperPropsAndId({
    wrapperProps,
    id: fieldProps.id,
  })

  const translate = useTranslate()

  const handleResetValue = () => {
    setInputKey(crypto.randomUUID())
    onChange([])
  }

  return (
    <InputWrapper {...internalWrapperProps}>
      <label
        htmlFor={id}
        className={cx(
          buttonStyles({
            variant: 'secondary',
            disabled: !!fieldProps.disabled,
            isFullWidth: false,
            isDanger: isInError ?? false,
          }),
          'flex items-center gap-x-1 w-full',
        )}
        role="button"
      >
        <Icon name="ArrowUpTray" color="unset" />
        <div className="grow min-w-0">
          {value?.length
            ? value.map((file) => (
                <div key={file.name} className="truncate text-ellipsis">
                  {file.name}
                </div>
              ))
            : (uploadMessage ?? translate('upload'))}
        </div>
        {!!value?.length && (
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              handleResetValue()
            }}
            iconName="Trash"
            isDanger={isInError ?? false}
            className="-m-1"
          />
        )}
      </label>
      <input
        key={inputKey}
        {...fieldProps}
        id={id}
        type="file"
        data-autofocus
        className="hidden"
        onChange={(evt: ChangeEvent<HTMLInputElement>) => {
          const nextValues = [...new Array(evt.target?.files?.length)].map(
            (_, index) => evt.target?.files?.item(index) as File,
          )
          onChange(nextValues)
        }}
        onClick={onTouched}
      />
    </InputWrapper>
  )
}
